import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { ConfigurationService } from '../shared/services/configuration.service';
import {
  AssemblyOrderModel,
  AssemblyOrderScheduleLogModel,
} from '../models/assembly-order.model';
import { Guid } from 'guid-typescript';
import { ItemCategoryModel } from '../models/item-category.model';
import { AssemblyTaskModel } from '../models/assembly-task.model';

@Injectable({
  providedIn: 'root',
})
export class AssemblyOrderService {
  private readonly http = inject(HttpClient);
  private readonly configSvc = inject(ConfigurationService);

  private readonly backUrl;
  private readonly popopSubject = new Subject<void>();

  constructor() {
    this.backUrl = `${this.configSvc.manufacturingApiUrl}api`;
  }

  getPopupState(): Observable<void> {
    return this.popopSubject.asObservable();
  }

  notifyPopupOpen(): void {
    this.popopSubject.next();
  }

  getAssemblyOrderByExternalId(
    extId: string
  ): Observable<Array<AssemblyOrderModel>> {
    return this.http.get<Array<AssemblyOrderModel>>(
      `${this.backUrl}/AssemblyOrder/externalIdentifier/${extId}`
    );
  }

  async enableDisableOA(
    id: string,
    paid: boolean,
    paymentDate: string
  ): Promise<Array<AssemblyOrderModel>> {
    try {
      const response: any = await firstValueFrom(
        this.http.patch<Array<AssemblyOrderModel>>(
          `${this.backUrl}/AssemblyOrder/${id}/payment`,
          { paid: paid, paymentDate: paymentDate },
          { observe: 'response' }
        )
      );

      return response && (response.status === 200 || response.status === 202);
    } catch (error) {
      console.error('An error occurred:', error);
      return [];
    }
  }

  getAssemblyOrderById(id: string): Observable<AssemblyOrderModel> {
    return this.http.get<AssemblyOrderModel>(
      `${this.backUrl}/AssemblyOrder/${id}`
    );
  }

  getAssemblyOrders(
    from: string,
    to: string
  ): Observable<Array<AssemblyOrderModel>> {
    //Returning mocked data: return of(assemblyOrders).pipe(delay(500));

    return this.http.get<Array<AssemblyOrderModel>>(
      `${this.backUrl}/AssemblyOrder?FromDate=${encodeURIComponent(from)}&ToDate=${encodeURIComponent(to)}`
    );
  }

  getAssemblyTaskLogs(
    id: Guid,
    itemCategoryId: Guid
  ): Observable<Array<AssemblyOrderModel>> {
    return this.http.get<Array<any>>(
      `${this.backUrl}/AssemblyActivity?AssemblyOrderId=${id}&itemCategoryId=${itemCategoryId}`
    );
  }

  getScheduleTaskLogs(
    id: Guid
  ): Observable<Array<AssemblyOrderScheduleLogModel>> {
    return this.http.get<Array<AssemblyOrderScheduleLogModel>>(
      `${this.backUrl}/AssemblyActivity/schedule-change-logs?AssemblyOrderId=${id}`
    );
  }

  async updateItemStatus(id: Guid, statusId: number): Promise<boolean> {
    try {
      const response: any = await firstValueFrom(
        this.http.patch(
          `${this.backUrl}/AssemblyTask/${id}/status`,
          { status: statusId },
          { observe: 'response' }
        )
      );

      return response && (response.status === 200 || response.status === 202);
    } catch (error) {
      console.error('An error occurred:', error);
      return false;
    }
  }

  async updatePoolShellStatus(id: Guid, statusId: number): Promise<boolean> {
    try {
      const response: any = await firstValueFrom(
        this.http.patch(
          `${this.backUrl}/AssemblyTask/${id}/PoolShellStatus`,
          { status: statusId },
          { observe: 'response' }
        )
      );

      return response && (response.status === 200 || response.status === 202);
    } catch (error) {
      console.error('An error occurred:', error);
      return false;
    }
  }

  async updatePoolShellTransit(id: Guid, transit: boolean): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.http.patch<{ inTransit: boolean }>(
          `${this.backUrl}/AssemblyTask/${id}/intransit`,
          { inTransit: transit },
          { observe: 'response' }
        )
      );
      return response && (response.status === 200 || response.status === 202);
    } catch (error) {
      console.error('An error occurred:', error);
      return false;
    }
  }

  // Cargar template de JSON para OA
  cargarJson(): Observable<any> {
    return this.http.get<any>('assets/json-template/json-oa-template.json');
  }

  // Enviar JSON OA
  enviarOAJSON(jsonOA: string): Observable<any> {
    return this.http.post(`${this.backUrl}/AssemblyOrder/`, JSON.parse(jsonOA));
  }

  // update warrantyNumber
  updateWarrantyNumber(externalIdentifier: string, warrantyNumber: string) {
    return this.http.patch(
      `${this.backUrl}/assemblyOrder/externalIdentifier/${externalIdentifier}/warrantyNumber`,
      { warrantyNumber: warrantyNumber }
    );
  }

  // Update observations
  updateObservations(orderId: Guid, observations: string) {
    return this.http.patch(
      `${this.backUrl}/AssemblyOrder/${orderId}/observation`,
      { observation: observations }
    );
  }

  // SAP Category Itmes
  getItemCategories(): Observable<ItemCategoryModel[]> {
    return this.http.get<ItemCategoryModel[]>(`${this.backUrl}/ItemCategories`);
  }

  getItemCategoryById(itemId: string): Observable<ItemCategoryModel> {
    return this.http.get<ItemCategoryModel>(
      `${this.backUrl}/ItemCategories/${itemId}`
    );
  }

  createItemCategory(category: ItemCategoryModel): Observable<any> {
    return this.http.post(`${this.backUrl}/ItemCategories`, category);
  }

  updateItemCategory(category: ItemCategoryModel): Observable<any> {
    return this.http.patch(
      `${this.backUrl}/ItemCategories/${category.id}`,
      category
    );
  }

  updatefilterSerialNumber(
    externalIdentifier: string,
    serialNumber: number
  ): Observable<any> {
    return this.http.patch(
      `${this.backUrl}/assemblyOrder/externalIdentifier/${externalIdentifier}/filterSerialNumber`,
      {
        filterSerialNumber: serialNumber,
      }
    );
  }

  addAssemblyTask(
    payload: Partial<AssemblyTaskModel> & { assemblyOrderGroupId: string }
  ): Observable<AssemblyTaskModel> {
    return this.http.post<AssemblyTaskModel>(
      `${this.backUrl}/AssemblyTask`,
      payload
    );
  }

  updateAssemblyTask(
    id: string,
    payload: Partial<AssemblyTaskModel>
  ): Observable<AssemblyTaskModel> {
    return this.http.patch<AssemblyTaskModel>(
      `${this.backUrl}/AssemblyTask/${id}`,
      payload
    );
  }

  deleteAssemblyTask(id: string): Observable<void> {
    return this.http.delete<void>(`${this.backUrl}/AssemblyTask/${id}`);
  }

  updatevalveSelectorSerialNumber(
    externalIdentifier: string,
    serialNumber: number
  ): Observable<any> {
    return this.http.patch(
      `${this.backUrl}/assemblyOrder/externalIdentifier/${externalIdentifier}/valveSelectorSerialNumber`,
      {
        valveSelectorSerialNumber: serialNumber,
      }
    );
  }
}
